import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 700px;

  .inner {
    display: flex;

    @media (max-width: 600px) {
      display: block;
    }

    // Prevent container from jumping
    @media (min-width: 700px) {
      min-height: 340px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  @media (max-width: 600px) {
    display: flex;
    overflow-x: auto;
    width: calc(100% + 80px);
    padding-left: 40px;
    margin-left: -40px;
    margin-bottom: 30px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
  @media (max-width: 480px) {
    width: calc(100% + 40px);
    padding-left: 20px;
    margin-left: -20px;
  }

  li {
    &:first-of-type {
      @media (max-width: 600px) {
        margin-left: 40px;
      }
      @media (max-width: 480px) {
        margin-left: 20px;
      }
    }
    &:last-of-type {
      @media (max-width: 600px) {
        padding-right: 40px;
      }
      @media (max-width: 480px) {
        padding-right: 20px;
      }
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: var(--tab-height);
  padding: 12px 20px;
  border-left: 2px solid var(--lightest-navy);
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--slate)')};
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  text-align: left;
  white-space: normal;
  line-height: 1.4;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 10px 15px;
  }
  @media (max-width: 600px) {
    ${({ theme }) => theme.mixins.flexCenter};
    min-width: 100px;
    max-width: 140px;
    padding: 12px 15px;
    border-left: 0;
    border-bottom: 2px solid var(--lightest-navy);
    text-align: center;
    white-space: normal;
    line-height: 1.3;
    font-size: 11px;
  }
  @media (max-width: 380px) {
    min-width: 90px;
    max-width: 120px;
    padding: 10px 12px;
    font-size: 10px;
  }

  &:hover,
  &:focus {
    background-color: var(--light-navy);
    transform: translateX(2px);
  }

  @media (max-width: 600px) {
    &:hover,
    &:focus {
      transform: none;
    }
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  border-radius: var(--border-radius);
  background: var(--green);
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;

  @media (max-width: 600px) {
    top: auto !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 2px !important;
    margin-left: 40px;
    transform: translateX(0) !important;
    left: 0;
  }
  @media (max-width: 480px) {
    margin-left: 20px;
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  margin-left: 20px;

  @media (max-width: 600px) {
    margin-left: 0;
  }
`;

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 25px 15px;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }

  @media (max-width: 480px) {
    padding: 15px 8px;
  }

  ul {
    ${({ theme }) => theme.mixins.fancyList};
    
    @media (max-width: 480px) {
      font-size: var(--fz-md);
      
      li {
        padding-left: 25px;
        margin-bottom: 8px;
      }
    }
  }

  h3 {
    margin-bottom: 8px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: var(--fz-xl);
    }

    @media (max-width: 480px) {
      font-size: var(--fz-lg);
      margin-bottom: 6px;
    }

    .company {
      color: var(--green);
    }
  }

  .range {
    margin-bottom: 30px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);

    @media (max-width: 768px) {
      margin-bottom: 20px;
    }

    @media (max-width: 480px) {
      margin-bottom: 15px;
      font-size: 11px;
    }
  }

  > div:last-child {
    margin-top: 20px;

    @media (max-width: 480px) {
      margin-top: 15px;
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const tabListRef = useRef(null);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  // Only re-run the effect if tabFocus changes
  useEffect(() => focusTab(), [tabFocus]);

  // Focus on tabs when using up & down arrow keys
  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  // Calculate highlight bar position
  const getHighlightStyle = () => {
    let transform = 'translateY(0)';
    let height = 'var(--tab-height)';
    let width = '2px';
    let top = '0';
    let bottom = 'auto';
    let left = '0';

    // Check if we're on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

    if (isMobile && tabs.current[activeTabId]) {
      // Mobile: horizontal positioning
      const activeTab = tabs.current[activeTabId];
      const tabListRect = tabListRef.current?.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      
      if (tabListRect && tabRect) {
        const offsetLeft = tabRect.left - tabListRect.left;
        transform = `translateX(${offsetLeft}px)`;
        width = `${tabRect.width}px`;
        height = '2px';
        top = 'auto';
        bottom = '0';
        left = '0';
      }
    } else if (!isMobile) {
      // Desktop: vertical positioning (fixed height for jobs)
      transform = `translateY(calc(${activeTabId} * var(--tab-height)))`;
      height = 'var(--tab-height)';
    }

    return { transform, height, width, top, bottom, left };
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I’ve Worked</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label="Job tabs" onKeyDown={e => onKeyDown(e)}>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => setActiveTabId(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i ? true : false}
                  aria-controls={`panel-${i}`}>
                  <span>{company}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link">
                          {company}
                        </a>
                      </span>
                    </h3>

                    <p className="range">{range}</p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
